import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, Spinner } from '@heroui/react';

import { useBluebirdChat } from '../hooks/useBluebirdChat';
import { useLiveEvents } from '../hooks/useLiveEvents';
import { MessageList } from './MessageList';
import { Composer } from './Composer';
import { LiveEventsPanel } from './LiveEventsPanel';

interface ChatPaneProps {
  sessionId: string;
  isActive: boolean;
}

/**
 * One Bluebird session — owns its own WebSocket, message stream, and
 * sandbox-broadcast feed. Layout splits horizontally: chat conversation
 * on the left (primary), collapsible live-events feed on the right.
 *
 * `isActive` only controls whether THIS pane is currently visible;
 * the WebSocket stays connected either way so background tabs keep
 * streaming.
 */
export function ChatPane({ sessionId, isActive }: ChatPaneProps) {
  const { agent, chat, isStreaming, connectionState } = useBluebirdChat(sessionId);
  const liveEvents = useLiveEvents(agent);
  const isConnected = connectionState === 'open';
  const [showEvents, setShowEvents] = useState(false);

  // Stop UX state: clicking Stop fires `cf_agent_chat_request_cancel`,
  // but if a tool is mid-execution (e.g. a 30 s browser_navigate) the
  // server can't acknowledge until the tool returns, so `isStreaming`
  // doesn't immediately flip. Track a local "stopping" flag so the
  // composer can swap to a disabled "Stopping…" affordance the
  // millisecond the user clicks.
  const [isStopping, setIsStopping] = useState(false);
  const wasStreamingRef = useRef(false);
  useEffect(() => {
    // Clear the stopping flag once the server-side stream actually ends.
    if (wasStreamingRef.current && !isStreaming) {
      setIsStopping(false);
    }
    wasStreamingRef.current = isStreaming;
  }, [isStreaming]);

  const handleSend = useCallback(
    (text: string, files?: File[]) => {
      const trimmed = text.trim();
      const hasFiles = !!files && files.length > 0;
      // Allow files-only sends ("describe this image") so attachments
      // alone are a valid prompt. Pure-empty sends are still gated.
      if (!trimmed && !hasFiles) return;
      // AI SDK v6's `sendMessage` accepts `{ text, files }` where
      // `files` is typed as `FileList | FileUIPart[]`. We hold raw
      // `File[]` from the composer; pack them into a synthetic
      // `FileList` via `DataTransfer.items.add` so the SDK's
      // base64-encoding path runs. AI SDK then emits one
      // `FileUIPart` per attachment in the resulting UI message,
      // which the Bluebird DO round-trips into Anthropic's image /
      // document content blocks server-side.
      //
      // Files-only path: omit `text` from the `sendMessage` arg
      // entirely. Passing `text: ''` would still emit a
      // `{ type: 'text', text: '' }` UIMessage part alongside the
      // file parts (AI SDK v6 includes the text part whenever the
      // property is present, regardless of value), which can
      // surface to the upstream provider as a stray empty user
      // turn. The discriminated union here matches the SDK's
      // `{ files }` overload that doesn't accept `text`.
      if (hasFiles) {
        const dt = new DataTransfer();
        for (const f of files!) dt.items.add(f);
        if (trimmed.length > 0) {
          chat.sendMessage({ text, files: dt.files });
        } else {
          chat.sendMessage({ files: dt.files });
        }
      } else {
        chat.sendMessage({ text });
      }
    },
    [chat],
  );

  const handleStop = useCallback(() => {
    setIsStopping(true);
    chat.stop();
  }, [chat]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1">
        {/* Conversation */}
        <section
          className="flex min-h-0 min-w-0 flex-1 flex-col"
          aria-label="Conversation"
        >
          <div className="flex items-center gap-2 border-b border-white/8 px-4 py-2 text-xs">
            <ConnectionDot connectionState={connectionState} streaming={isStreaming} />
            <span className="opacity-60" translate="no">
              session: <code>{sessionId}</code>
            </span>
            <div className="ms-auto flex items-center gap-2">
              {chat.messages.length > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onPress={() => chat.clearHistory()}
                >
                  Clear
                </Button>
              )}
              <Button
                size="sm"
                variant={showEvents ? 'outline' : 'ghost'}
                onPress={() => setShowEvents((v) => !v)}
                aria-pressed={showEvents}
              >
                Live events {liveEvents.length > 0 ? `(${liveEvents.length})` : ''}
              </Button>
            </div>
          </div>

          <MessageList
            messages={chat.messages}
            isStreaming={isStreaming}
            isActive={isActive}
            status={chat.status}
            sessionId={sessionId}
          />

          <Composer
            // Don't gate on connection state — the user can compose
            // while the socket is reconnecting; useAgentChat queues
            // and partysocket auto-reconnects with backoff. Send is
            // only disabled mid-stream (which swaps to Stop below).
            disabled={false}
            isStreaming={isStreaming}
            isStopping={isStopping}
            isReconnecting={!isConnected && !isStreaming}
            onSend={handleSend}
            onStop={handleStop}
          />
        </section>

        {/* Live events drawer */}
        {showEvents && (
          <aside
            aria-label="Live sandbox events"
            className="flex w-96 min-w-72 max-w-[40%] flex-col border-s border-white/8 bg-white/2"
          >
            <LiveEventsPanel events={liveEvents} onClose={() => setShowEvents(false)} />
          </aside>
        )}
      </div>
    </div>
  );
}

function ConnectionDot({
  connectionState,
  streaming,
}: {
  connectionState: 'connecting' | 'open' | 'closing' | 'closed';
  streaming: boolean;
}) {
  let label: string;
  let color: string;
  if (streaming) {
    label = 'Streaming';
    color = 'bg-blue-400 shadow-[0_0_8px] shadow-blue-400/60';
  } else if (connectionState === 'open') {
    label = 'Connected';
    color = 'bg-emerald-400';
  } else if (connectionState === 'connecting') {
    label = 'Connecting…';
    color = 'bg-amber-400 animate-pulse';
  } else {
    // 'closing' / 'closed' — partysocket auto-reconnects, but surface it
    // so the user knows why responses might be delayed.
    label = 'Reconnecting…';
    color = 'bg-rose-400 animate-pulse';
  }
  return (
    <span className="inline-flex items-center gap-1.5" aria-live="polite">
      <span aria-hidden="true" className={`h-2 w-2 rounded-full ${color}`} />
      <span className="opacity-70">{label}</span>
      {streaming && <Spinner size="sm" aria-label="Streaming" />}
    </span>
  );
}
